// src/pages/ForgotPassword.jsx
// Public route (no session). Reached from Login.jsx's "Forgot password?"
// link, which was previously a dead href="#" — no page existed here at all.
//
// Deliberately shows the SAME confirmation message regardless of whether
// the submitted email matches a real account — the backend
// (/api/auth/request-password-reset, see main.py) is built the same way,
// so this page mirroring that on the frontend isn't just cosmetic: showing
// a different message here for "email not found" would undo the backend's
// enumeration-safety design by leaking the same information through the UI
// instead of the API response.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { requestPasswordReset } from '../api/kailux'
import ErrorMessage from '../components/ErrorMessage'


import { T, alpha } from '../theme/theme'
export default function ForgotPassword() {
  const [email, setEmail]     = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [sent, setSent]       = useState(false)

  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!email.trim()) { setError('Please enter your email address.'); return }
    setLoading(true); setError('')
    try {
      await requestPasswordReset(email.trim())
    } catch {
      // Network/5xx only — the backend itself never returns a different
      // shape for "no such account", so a caught error here is a genuine
      // delivery problem, not a signal about the email's validity.
    } finally {
      setLoading(false)
      setSent(true)
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>

      <div style={{ position:'fixed', top:'20%', left:'50%', transform:'translateX(-50%)',
        width:'600px', height:'300px', pointerEvents:'none',
        background:`radial-gradient(ellipse,${alpha(T.or, 0.08)} 0%,transparent 70%)` }}/>

      <div style={{ width:'100%', maxWidth:'420px', position:'relative', zIndex:1 }}>

        <div style={{ textAlign:'center', marginBottom:'36px' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:'8px' }}>
            <div style={{ width:'40px', height:'40px', borderRadius:'12px',
              background:T.prg,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:"'Syne',sans-serif", fontWeight:'800',
              color:T.onpri, fontSize:'18px' }}>K</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'24px', fontWeight:'800' }}>
              Kai<span style={{ color:T.or }}>Lux</span>
            </div>
          </div>
        </div>

        <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
          borderRadius:'20px', padding:'32px',
          boxShadow:T.shl }}>

          {sent ? (
            <>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
                fontWeight:'800', marginBottom:'10px' }}>Check your email</div>
              <div style={{ fontSize:'14px', color:T.mt, lineHeight:1.6, marginBottom:'24px' }}>
                If an account exists for <strong style={{ color:T.tx }}>{email.trim()}</strong>,
                we've sent a link to reset your password. The link expires in 1 hour.
              </div>
              <Link to="/login" style={{ fontSize:'14px', color:T.or, fontWeight:'700',
                textDecoration:'none' }}>← Back to login</Link>
            </>
          ) : (
            <>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
                fontWeight:'800', marginBottom:'6px' }}>Reset your password</div>
              <div style={{ fontSize:'14px', color:T.mt, marginBottom:'28px' }}>
                Enter the email address on your KaiLux account and we'll send you a reset link.
              </div>

              {error && <ErrorMessage style={{ marginBottom:'16px' }}>{error}</ErrorMessage>}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom:'20px' }}>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                    EMAIL ADDRESS
                  </label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                    placeholder="you@yourfirm.co.za" autoComplete="email" autoFocus
                    style={{ width:'100%', padding:'12px 14px', background:T.s2,
                      border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                      fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                      outline:'none', boxSizing:'border-box' }}
                    onFocus={e=>e.target.style.borderColor=T.or}
                    onBlur={e=>e.target.style.borderColor=T.bd}/>
                </div>

                <button type="submit" disabled={loading}
                  style={{ width:'100%', padding:'12px', borderRadius:'8px', border:'none',
                    background:loading?T.mt:T.prg,
                    color:T.onpri, fontFamily:"'Outfit',sans-serif",
                    fontSize:'15px', fontWeight:'700', cursor:loading?'not-allowed':'pointer',
                    boxShadow:loading?'none':`0 4px 20px ${alpha(T.pri, 0.35)}`,
                    display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                  {loading ? (
                    <>
                      <span style={{ width:'14px', height:'14px',
                        border:`2px solid ${alpha(T.onpri, 0.3)}`,
                        borderTopColor:T.onpri, borderRadius:'50%',
                        animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                      Sending...
                    </>
                  ) : 'Send reset link →'}
                </button>
              </form>

              <div style={{ textAlign:'center', marginTop:'20px', fontSize:'14px', color:T.mt }}>
                <Link to="/login" style={{ color:T.or, fontWeight:'700',
                  textDecoration:'none' }}>← Back to login</Link>
              </div>
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
